import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Extension } from "@tiptap/react";
import { filterAcceptedFiles } from "@/lib/file-drag";

function acceptedFilesFromList(files: FileList | null | undefined, accept?: string): File[] {
  return filterAcceptedFiles(Array.from(files ?? []), accept ?? "image/*");
}

function dragHasFiles(event: DragEvent): boolean {
  const types = event.dataTransfer?.types;
  return types ? Array.from(types).includes("Files") : false;
}

/**
 * Handles attachment *pastes* into the editor, and makes the editor inert to file *drops*. The composer
 * owns drag-and-drop at the card level (see `MessageComposerLayout`'s dropzone), so files dropped
 * anywhere over the composer attach there. A file dropped directly on the editor is consumed here
 * (preventDefault + return true) so ProseMirror's default drop handler never runs — otherwise it
 * would move the selection / mutate the doc at the drop point and wipe the draft text. We do NOT
 * upload from this handler; the composer's bubbled drop handler performs the single upload.
 */
export function createAttachmentPasteDropExtension(args: {
  disabled?: boolean;
  accept?: string;
  onFilesSelected?: (files: File[]) => void | Promise<void>;
}) {
  const enabled = () => Boolean(args.onFilesSelected) && !args.disabled;
  return Extension.create({
    name: "attachmentPasteDrop",
    addProseMirrorPlugins() {
      return [
        new Plugin({
          key: new PluginKey("attachmentPasteDrop"),
          props: {
            handlePaste: (_view, event) => {
              const files = acceptedFilesFromList(event.clipboardData?.files, args.accept);
              if (!args.onFilesSelected || args.disabled || files.length === 0) {
                return false;
              }
              event.preventDefault();
              void args.onFilesSelected(files);
              return true;
            },
            handleDOMEvents: {
              drop: (_view, event) => {
                if (!enabled() || !dragHasFiles(event)) {
                  return false;
                }
                // Block ProseMirror's default drop (which would clobber the draft) and the browser's
                // native contenteditable drop. The composer-level handler does the actual upload.
                event.preventDefault();
                return true;
              },
            },
          },
        }),
      ];
    },
  });
}
